import { ReactElement, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { Route, Routes, useNavigate, useLocation } from 'react-router';
import noop from 'lodash/noop';

// Components.
import { AccountSettings, LoadingOverlay, Toast, ViewTranscripts } from 'components';
import { Footer } from './Footer';
import { Header } from './Header';
import { ActivitySearchPage } from '../pages/Activity/ActivitySearch';
import { ActivityDetail } from '../pages/Activity/ActivityDetail';
import AuthenticatedRoute from '../components/AuthenticatedRoute';

// Hooks.
import { useAuth } from 'hooks';

// Pages.
import { ActivityPage, HomePage, NotFoundPage, ProfilePage, RegisterPage, LoginPage } from 'pages';

// Styles.
import styles from './App.module.scss';

// Store + Core
import { userProfileSelector } from 'store/profile/selectors';
import { getUserProfile } from 'store/profile/actions';
import { popToast } from 'store/toast/actions';
import { errorToastOptions } from 'store/toast/constants';
import { getTaxonomyTermById } from 'store/taxonomy/actions';
import { helpdeskTopicsTermSelector } from 'store/taxonomy/selectors';
import { IUserProfile, TAGS_ENUM, TAXONOMY_HELPDESK_TOPICS_ROOT_ID, useAppDispatch } from 'core';

// Types
import { AnalyticsService } from 'services/AnalyticsService';

export function App(): ReactElement {
  const [, { isAuthenticated, isLoaded }, getAccessToken] = useAuth();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Selectors
  const userProfile: IUserProfile = useSelector(userProfileSelector);
  const helpDeskTopics = useSelector(helpdeskTopicsTermSelector);

  const isPendingProfile: boolean = isAuthenticated && !userProfile;
  const isLoading: boolean = isPendingProfile || !isLoaded;

  useEffect(() => {
    if (isPendingProfile) {
      const fetchUserData = async (): Promise<void> => {
        await getAccessToken();
        await dispatch(getUserProfile());
      };
      fetchUserData().then(noop);
    }
  }, [isAuthenticated, userProfile, isPendingProfile]);

  useEffect(() => {
    // Scroll to top when the route has changed.
    window.scroll(0, 0);
  }, [location]);

  useEffect(() => {
    dispatch(getTaxonomyTermById(TAXONOMY_HELPDESK_TOPICS_ROOT_ID));
  }, [dispatch]);

  // 5350 Display login error message when profile load fails.
  useEffect(() => {
    const queryStringParams = new URLSearchParams(location.search);
    // Read query string value
    const loginFailedValue = queryStringParams.get('isloginfailed');

    const loginErrorMessage = helpDeskTopics?.filter((i) => i.tag === TAGS_ENUM.HELP_DESK__LOG_IN_ISSUES)[0]
      ?.description;

    if (loginErrorMessage && loginFailedValue === '1') {
      dispatch(
        popToast({
          ...errorToastOptions,
          message: <>{loginErrorMessage}</>,
        }),
      );
      queryStringParams.delete('isloginfailed');
      navigate(
        {
          search: queryStringParams.toString(),
        },
        { replace: true },
      );
    }
  });

  useEffect(() => {
    // track page views basd on URL change since we can't do it via AuthenticatedRoute
    AnalyticsService.trackPageView();
  }, [location.pathname, location.search, location.hash]);

  if (isLoading) return <LoadingOverlay isOpen />;

  return (
    <>
      <Helmet>
        <title>CME Passport</title>
      </Helmet>
      <Header />
      <main className={styles.main}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/profile"
            element={
              <AuthenticatedRoute requiresAuth>
                <ProfilePage />
              </AuthenticatedRoute>
            }
          >
            <Route
              path="account-settings"
              element={
                <AuthenticatedRoute requiresAuth>
                  <AccountSettings />
                </AuthenticatedRoute>
              }
            />
            <Route
              path="transcripts"
              element={
                <AuthenticatedRoute requiresAuth>
                  <ViewTranscripts />
                </AuthenticatedRoute>
              }
            />
          </Route>
          <Route
            path="/register"
            element={
              <AuthenticatedRoute requiresGuest>
                <RegisterPage />
              </AuthenticatedRoute>
            }
          />
          <Route path="/activity" element={<ActivityPage />}>
            <Route path="search" element={<ActivitySearchPage />} />
            <Route path="search/:curatedQuery" element={<ActivitySearchPage />} />
            <Route path="details/:id" element={<ActivityDetail />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      <Toast />
    </>
  );
}
